import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import EmptyState from './EmptyState';
import type { DailyPnlPoint } from '../../types/analytics';

const PAGE_SIZE = 20;

/** Generate every ISO date in [start, end] inclusive. start/end must be
 *  `YYYY-MM-DD`. Uses UTC arithmetic to avoid TZ drift across DST. */
function eachDate(start: string, end: string): string[] {
  const out: string[] = [];
  if (!start || !end) return out;
  const toUtc = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const startMs = toUtc(start);
  const endMs = toUtc(end);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return out;
  const dayMs = 24 * 60 * 60 * 1000;
  for (let t = startMs; t <= endMs; t += dayMs) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** Weekday abbreviation (MON…SUN) for a `YYYY-MM-DD` ISO date. UTC to match eachDate. */
function dayAbbr(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? '';
}

const fmtInr = (n: number): string => {
  if (n === 0) return '₹0';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const fmtInrSigned = (n: number): string => (n >= 0 ? '+' : '') + fmtInr(n);

const colorClass = (n: number): string =>
  n > 0 ? 'text-emerald-600 dark:text-emerald-400'
       : n < 0 ? 'text-red-600 dark:text-red-400'
       : 'text-gray-700 dark:text-gray-300';

/**
 * Tabular Daily P&L for the Overview / Strategies drill-down sections.
 * Shows only days that had closed trades — zero-trade calendar days are
 * omitted. Cumulative is the running P&L total as of each trading day:
 * computed over the full calendar sequence (so omitted zero-days don't
 * distort the running sum), then filtered down to the traded days.
 *
 * `points` is sparse (one entry per day with closed trades).
 *
 * Columns: Date · Day · Daily P&L · Trades · Cumulative.
 */
const DailyPnlTable: React.FC<{
  points: DailyPnlPoint[];
  rangeStart: string;
  rangeEnd: string;
}> = ({ points, rangeStart, rangeEnd }) => {
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const byDate = new Map(points.map((p) => [p.date, p]));
    const dates = eachDate(rangeStart, rangeEnd);
    let running = 0;
    const dense = dates.map((d) => {
      const point = byDate.get(d);
      const pnl = point?.pnl ?? 0;
      running += pnl;
      return {
        date: d,
        day: dayAbbr(d),
        pnl,
        trades: point?.trades ?? 0,
        cumulative: running,
      };
    });
    // Show only days that traded — running cumulative already accounts for the
    // omitted zero-trade days (they add 0), so each row's cumulative stays correct.
    return dense.filter((r) => r.trades > 0).reverse();
  }, [points, rangeStart, rangeEnd]);

  if (!rows.length) return <EmptyState message="No P&L recorded in this range." />;

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const realVisible = rows.slice(start, start + PAGE_SIZE);
  // Pad to PAGE_SIZE so the table height stays constant when the last page
  // has fewer than PAGE_SIZE rows. Without this, navigating Prev/Next causes
  // the left column to grow/shrink, which cascades to the right-column charts.
  const padCount = PAGE_SIZE - realVisible.length;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
              <th className="text-left  px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Date</th>
              <th className="text-left  px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Day</th>
              <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Daily P&amp;L</th>
              <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
              <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Cumulative</th>
            </tr>
          </thead>
          <tbody>
            {realVisible.map((r) => (
              <tr
                key={r.date}
                className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/60"
              >
                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-700 dark:text-gray-300">{r.date}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{r.day}</td>
                <td className={`px-3 py-2 whitespace-nowrap text-right tabular-nums font-semibold ${colorClass(r.pnl)}`}>
                  {fmtInrSigned(r.pnl)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {r.trades}
                </td>
                <td className={`px-3 py-2 whitespace-nowrap text-right tabular-nums ${colorClass(r.cumulative)}`}>
                  {fmtInrSigned(r.cumulative)}
                </td>
              </tr>
            ))}
            {/* Pad to PAGE_SIZE rows so a partial last page doesn't collapse
                the table's height. Empty cells use a non-breaking space so
                their padding contributes the same row height as filled rows. */}
            {Array.from({ length: padCount }).map((_, i) => (
              <tr
                key={`pad-${i}`}
                aria-hidden="true"
                className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/60"
              >
                <td className="px-3 py-2 whitespace-nowrap text-gray-300 dark:text-gray-600">&nbsp;</td>
                <td className="px-3 py-2 whitespace-nowrap">&nbsp;</td>
                <td className="px-3 py-2 whitespace-nowrap">&nbsp;</td>
                <td className="px-3 py-2 whitespace-nowrap">&nbsp;</td>
                <td className="px-3 py-2 whitespace-nowrap">&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-xs">
        <div className="text-gray-500 dark:text-gray-400">
          Showing <span className="font-medium text-gray-700 dark:text-gray-300">{start + 1}–{Math.min(start + PAGE_SIZE, rows.length)}</span>
          {' '}of{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{rows.length}</span> trading days
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </button>
          <span className="text-gray-500 dark:text-gray-400 tabular-nums">
            Page {safePage + 1} of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={safePage >= pageCount - 1}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyPnlTable;
